import { useState, useEffect } from 'react';
import { Box, Flex, Text, IconButton, Badge } from '@radix-ui/themes';
import { LayersIcon, CounterClockwiseClockIcon } from '@radix-ui/react-icons';
import { motion, AnimatePresence } from 'framer-motion';

const LessonRangeSelector = ({
    lessons = [],
    onLessonSelectionChange,
    selectedLessonStructureId
}) => {
    const [selectedLessons, setSelectedLessons] = useState([]);
    const [selectionMode, setSelectionMode] = useState('range');

    useEffect(() => {
        console.log('lessons', lessons);
        const sortedLessons = [...lessons].sort((a, b) => a.order - b.order);
        setSelectedLessons(sortedLessons);

    }, [lessons]);

    useEffect(() => {
        console.log('selectedLessons', selectedLessons);
        onLessonSelectionChange(selectedLessons);
    }, [selectedLessons]);

    useEffect(() => {
        console.log('selectedLessonStructureId', selectedLessonStructureId);
        setSelectedLessons([]);
    }, [selectedLessonStructureId]);

    const handleLessonClick = (lessonId) => {
        setSelectedLessons(prev => {
            if (selectionMode === 'single') {
                if (prev.includes(lessonId)) {
                    return prev.filter(id => id !== lessonId);
                }
                return [...prev, lessonId];
            } else {
                // Range selection mode
                if (prev.length === 0) {
                    return [lessonId];
                }

                const lastSelected = prev[prev.length - 1];
                const allLessons = lessons.map(l => l.id);
                const startIdx = allLessons.indexOf(lastSelected);
                const endIdx = allLessons.indexOf(lessonId);

                const start = Math.min(startIdx, endIdx);
                const end = Math.max(startIdx, endIdx);

                return allLessons.slice(start, end + 1);
            }
        });
    };

    const handleToggleSelectionMode = () => {
        setSelectionMode(prev => prev === 'range' ? 'single' : 'range');
    };

    const handleClearSelection = () => {
        setSelectedLessons([]);
    };

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 24
            }
        }
    };

    return (
        <Box>
            <Flex direction="column" gap="2">
                <Flex direction="row" gap="2" justify="between" align="center">
                    <Text size="1" mb="1" style={{ color: 'var(--gray-11)' }}>
                        Select the lessons that will be included in this teaching plan.
                    </Text>
                    <Flex direction="row" gap="2">
                        <IconButton
                            variant={selectionMode === 'range' ? 'solid' : 'soft'}
                            onClick={(e) => {
                                e.preventDefault();
                                handleToggleSelectionMode();
                            }}
                        >
                            <LayersIcon />
                        </IconButton>
                        <IconButton
                            variant='soft'
                            onClick={(e) => {
                                e.preventDefault();
                                handleClearSelection();
                            }}
                        >
                            <CounterClockwiseClockIcon />
                        </IconButton>
                    </Flex>
                </Flex>

                <motion.div
                    key="lesson-container"
                    variants={container}
                    initial="hidden"
                    animate="show"
                >
                    <AnimatePresence mode="sync">
                        {lessons.map((lesson, index) => (
                            <motion.div
                                key={`${selectedLessonStructureId}-${lesson.id}`}
                                variants={item}
                                initial="hidden"
                                animate="show"
                                exit={{
                                    opacity: 0,
                                    y: 20,
                                    transition: {
                                        duration: 0.2,
                                        delay: index * 0.05
                                    }
                                }}
                                custom={index}
                                onClick={() => handleLessonClick(lesson.id)}
                                style={{
                                    padding: '5px 10px',
                                    border: '1px solid var(--gray-6)',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedLessons.includes(lesson.id)
                                        ? 'var(--accent-11)'
                                        : 'var(--gray-9)',
                                    marginBottom: '4px'
                                }}
                            ><Flex direction="row" gap="2" justify="between" align="center">
                                    <Text size="1" style={{ color: 'var(--gray-1)' }}>
                                        {lesson.title}
                                    </Text>
                                    <Box>
                                        <Badge variant="solid" color="jade" size="1">
                                            <Text size="1" style={{ color: 'var(--gray-1)' }}>
                                                {lesson.sub_strand.indicator}
                                            </Text>
                                        </Badge>
                                    </Box>
                                </Flex>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </Flex>
        </Box>
    );
};

export default LessonRangeSelector;
